import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { filter } from "rxjs";

const MIN_DISPLAY_MS = 300;

/**
 * Rutas de inicio de cada módulo que cargan dashboards con gráficos.
 * El loader global NO se activa al navegar a estas rutas para que el menú
 * sea accesible inmediatamente mientras los widgets cargan en segundo plano.
 */
const INICIO_ROUTE_PATTERN = /[/_]inicio(\?.*)?$/;

@Injectable({ providedIn: "root" })
export class GlobalLoadingService {
  public readonly isLoading = signal<boolean>(false);

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt: number | null = null;

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => this.onNavigationStart(e.url));

    this.router.events
      .pipe(
        filter(
          (e): e is NavigationEnd | NavigationCancel | NavigationError =>
            e instanceof NavigationEnd ||
            e instanceof NavigationCancel ||
            e instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.onNavigationEnd());
  }

  private onNavigationStart(url: string): void {
    // No bloquear el menú al navegar a pantallas de inicio/dashboard
    if (INICIO_ROUTE_PATTERN.test(url)) {
      return;
    }

    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (!this.isLoading()) {
      this.shownAt = Date.now();
      this.isLoading.set(true);
    }
  }

  private onNavigationEnd(): void {
    if (!this.isLoading()) {
      return;
    }

    const elapsed = this.shownAt !== null ? Date.now() - this.shownAt : MIN_DISPLAY_MS;
    const remaining = Math.max(MIN_DISPLAY_MS - elapsed, 0);

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.shownAt = null;
      this.isLoading.set(false);
    }, remaining);
  }
}
